import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, ArrowRight, Bookmark, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function getNextWednesdayLabel() {
  const d = new Date();
  const day = d.getDay();
  const diff = (3 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return {
    iso: d.toISOString().slice(0, 10),
    label: d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" }),
  };
}

export function WeeklyInicioWidget() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ nextWeekly: null, myItems: [], total: 0, urgent: 0 });
  const [loading, setLoading] = useState(true);
  const next = getNextWednesdayLabel();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const wRes = await fetch(`${API}/api/weeklys`, { headers });
        const weeklys = wRes.ok ? await wRes.json() : [];
        const upcoming = weeklys.find((w) => w.fecha >= new Date().toISOString().slice(0, 10)) || weeklys[0];

        let items = [];
        if (upcoming) {
          const iRes = await fetch(`${API}/api/weeklys/items/all?weekly_id=${upcoming.id}`, { headers });
          if (iRes.ok) items = await iRes.json();
        }
        // Filter by current user (responsable) + still pending
        const fullName = user?.full_name || "";
        const myItems = items.filter(
          (i) =>
            i.estado !== "hecho" &&
            (i.responsable === fullName ||
              (fullName && i.autor_nombre && fullName.toLowerCase().includes(i.autor_nombre.toLowerCase())))
        );
        const urgent = myItems.filter((i) => i.prioridad === "alta").length;
        if (!cancel) setData({ nextWeekly: upcoming, myItems, total: items.length, urgent });
      } catch (e) {
        // silent
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [token, user]);

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.25 }}
      onClick={() => navigate("/weeklys")}
      style={{
        background: "linear-gradient(135deg, rgba(0,121,52,0.12), rgba(131,198,26,0.06))",
        border: "1px solid rgba(0,121,52,0.4)",
        borderRadius: 16,
        padding: "22px 26px",
        marginBottom: 24,
        cursor: "pointer",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 22,
        transition: "border-color 0.2s, transform 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(131,198,26,0.6)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(0,121,52,0.4)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
      data-testid="weekly-inicio-widget"
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: "rgba(131,198,26,0.15)",
          border: "1px solid rgba(131,198,26,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <CalendarDays size={22} color="#83c61a" />
      </div>

      <div style={{ flex: "1 1 240px", minWidth: 240 }}>
        <p style={{ fontSize: 11, color: "rgba(131,198,26,0.85)", fontWeight: 600, letterSpacing: "0.1em", marginBottom: 4 }}>
          PRÓXIMA WEEKLY
        </p>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", textTransform: "capitalize", marginBottom: 4 }}>
          {next.label}
        </h3>
        <p style={{ fontSize: 12, color: "rgba(241,245,249,0.5)" }}>
          {data.nextWeekly?.titulo || "Sin weekly programada · 12:30–13:30"}
        </p>
      </div>

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Bookmark size={15} color="#83c61a" />
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", lineHeight: 1 }}>{data.myItems.length}</div>
            <div style={{ fontSize: 10, color: "rgba(241,245,249,0.45)", textTransform: "uppercase", letterSpacing: 1 }}>Tus puntos</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AlertTriangle size={15} color={data.urgent ? "#fbbf24" : "rgba(255,255,255,0.3)"} />
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: data.urgent ? "#fbbf24" : "#f1f5f9", lineHeight: 1 }}>
              {data.urgent}
            </div>
            <div style={{ fontSize: 10, color: "rgba(241,245,249,0.45)", textTransform: "uppercase", letterSpacing: 1 }}>Urgentes</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle2 size={15} color="#34d399" />
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", lineHeight: 1 }}>{data.total}</div>
            <div style={{ fontSize: 10, color: "rgba(241,245,249,0.45)", textTransform: "uppercase", letterSpacing: 1 }}>Total agenda</div>
          </div>
        </div>
      </div>

      <button
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          color: "#83c61a",
          border: "1px solid rgba(131,198,26,0.4)",
          borderRadius: 10,
          padding: "9px 16px",
          fontWeight: 600,
          fontSize: 13,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        Abrir Weeklys <ArrowRight size={14} />
      </button>
    </motion.div>
  );
}
